import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, forwardRef } from '@angular/core';
import { CsSelectOptionBase } from './cs-select-option-interface';
import { PopoverController } from 'providers/popover-controller';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { CsSelectPopover, CsSelectPopoverOption } from '../cs-select-popover/cs-select-popover';
import { BackdropMode } from 'components/popover-host/popover-host';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { syncButtonNameFromRow } from 'directives/cs-capture-cover/row-name-source';
import { DestroyGuard } from 'services/browser-utils';

@Component({
  selector: 'cs-select',
  templateUrl: 'cs-select.html',
  host: { 'class': 'select' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CsSelectComponent implements OnInit, OnDestroy {
  @ContentChildren(forwardRef(() => CsSelectOptionBase), { descendants: true }) options: QueryList<CsSelectOptionBase>;
  @Input() public multiple: boolean = false;
  @Input() public placeholder: TranslatedString = LL.common.placeholders.select();
  @Output() public selectionChange = new EventEmitter<any | any[]>();
  @Input() public value: any;
  @Output() public valueChange = new EventEmitter<any>();
  @Input() public disabled: boolean = false;
  @Input() public searchable: boolean = false;

  /**
   * What a screen reader announces for this select, when the row it sits in is not the right
   * name.
   *
   * The select draws its CURRENT VALUE and nothing that says what the value is for, so without
   * this input the inner `<button>` takes its name from the enclosing row's text through
   * `aria-labelledby` (see `syncButtonNameFromRow`) — which reads as the row's label followed by
   * the value, "Units Metric". Give it a name here when the select has no such row, or when
   * having the value read as part of the name reads badly.
   */
  @Input() public ariaLabel?: string;

  /** true while the options popover is open, so the button can report it through aria-expanded */
  popoverOpen: boolean = false;

  private wasSmallScreen: boolean;
  private resizeListener: () => void;
  private destroyGuard = new DestroyGuard();

  constructor(
    private popoverCtrl: PopoverController,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.wasSmallScreen = hasClientSmallScreen();

    // add resize listener to detect screen size changes
    this.resizeListener = () => {
      const isSmallScreen = hasClientSmallScreen();
      // if screen size category changed (small <-> large)
      if (this.wasSmallScreen !== isSmallScreen) {
        this.wasSmallScreen = isSmallScreen;
        this.cdr.markForCheck();
      }
    };

    window.addEventListener('resize', this.resizeListener);
  }

  ngAfterViewChecked() {
    // the inner <button> is empty, so without an ariaLabel it takes its name from the row's text
    syncButtonNameFromRow(this.elementRef, this.ariaLabel);
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  getLabel(): TranslatedString {
    const isSmallScreen = hasClientSmallScreen();
    if (this.multiple) {
      // each piece is already localized (or a non-translatable value/abbreviation);
      // joining them with a plain separator just composes the display text
      let label = this.options.filter((option) => option.isSelected()).map((option) =>
        isSmallScreen ? option.getAbbreviatedLabel() : option.getLabel()
      ).join(', ');
      return untranslatedString(label);
    } else {
      let selectedOption = this.options.find((option) => option.getValue() === this.value);
      if (selectedOption) {
        return isSmallScreen ? selectedOption.getAbbreviatedLabel() : selectedOption.getLabel();
      }
      // this.value is an arbitrary selection value (string/number/enum), not catalog text
      return untranslatedString(this.value || '');
    }
  }

  deselectAll() {
    this.options.forEach((item) => {
      item.deselect();
    });
  }

  handleSelectClick(event: Event) {
    lockClick(event, this.handleSelectClickAsync());
  }

  private async handleSelectClickAsync() {
    const isSmallScreen = hasClientSmallScreen();
    const containerEl = this.elementRef.nativeElement as HTMLElement;
    const containerWidth = containerEl.offsetWidth;

    // Transform options to the format expected by cs-select-popover
    const popoverOptions: CsSelectPopoverOption[] = this.options.map((option) => ({
      name: option.getLabel(),
      value: option.getValue(),
      contentTemplate: option.getContentTemplate(),
      disabled: option.isDisabled()
    }));

    // the list is open for exactly as long as the popover is presented, whichever way it closes
    this.popoverOpen = true;
    this.cdr.markForCheck();
    let result: any;
    try {
      result = await this.popoverCtrl.present(CsSelectPopover, {
        title: this.placeholder || LL.common.placeholders.selectAnOption(),
        options: popoverOptions,
        value: this.value,
        targetWidth: containerWidth,
        bottomSheet: isSmallScreen,
        searchable: this.searchable
      }, {
        targetFromElement: containerEl,
        targetToElement: containerEl,
        animate: true,
        bottomSheet: isSmallScreen,
        verticalAlign: 'bottom',
        overlayPreviousPopover: true,
        backdropMode: isSmallScreen ? BackdropMode.Visible : BackdropMode.Invisible,
      });
    } finally {
      this.popoverOpen = false;
      this.destroyGuard.safeMarkForCheck(this.cdr);
    }

    if (result && result.value !== undefined) {
      // Find the selected option and trigger its click handler
      const selectedOption = this.options.find(opt => opt.getValue() === result.value);
      if (selectedOption) {
        selectedOption.handleOptionClick();
        this.value = result.value;
        this.valueChange.emit(this.value);
        this.selectionChange.emit(result.value);
      }
    }
  }
}
